// Cave84 — Analytics (Phase 6 Week 17)
// Spec §4.12 F12.7–F12.12.
// Pure CSS charts — no external chart library.
// Data: full inventory join via useInventory({availableAtHome: false}).

import { useMemo } from 'react';
import { useInventory, useConsumedInventory, DEFAULT_FILTERS } from '@/hooks/useInventory';
import { useAppellationsMap } from '@/hooks/useAppellations';

const ALL_FILTERS = { ...DEFAULT_FILTERS, availableAtHome: false };

// ---------------------------------------------------------------------------
// CSS bar chart primitives
// ---------------------------------------------------------------------------

interface BarItem {
  label: string;
  value: number;
  color?: string;
}

function BarChart({ items, max, unit = '', color: defaultColor = 'bg-accent-burgundy' }: {
  items: BarItem[];
  max?: number;
  unit?: string;
  color?: string;
}) {
  const maxVal = max ?? Math.max(...items.map(i => i.value), 1);
  return (
    <div className="space-y-2">
      {items.map(({ label, value, color = 'bg-accent-burgundy' }) => (
        <div key={label} className="flex items-center gap-2">
          <p className="text-xs text-text-secondary w-32 flex-shrink-0 truncate text-right">{label}</p>
          <div className="flex-1 h-4 bg-surface-3 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${color ?? defaultColor}`}
              style={{ width: `${Math.max(2, (value / maxVal) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-text-muted w-8 flex-shrink-0 tabular-nums text-right">
            {value}{unit}
          </p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function AnalyticsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-1 border border-border-medium rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border-subtle">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">{title}</h2>
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// F12.7 — Geographic distribution (region of origin)
// ---------------------------------------------------------------------------

function GeoDistribution() {
  const { rows } = useInventory(ALL_FILTERS);
  const appellationsMap = useAppellationsMap();

  const items = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const appellation = appellationsMap.get(r.wine.appellation_id);
      const region = appellation?.region ?? 'Unknown';
      m.set(region, (m.get(region) ?? 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([label, value]) => ({ label, value }));
  }, [rows, appellationsMap]);

  if (!items.length) return <p className="text-xs text-text-muted">No data yet</p>;
  return <BarChart items={items} />;
}

// ---------------------------------------------------------------------------
// F12.9 — Vintage histogram
// ---------------------------------------------------------------------------

function VintageHistogram() {
  const { rows } = useInventory(ALL_FILTERS);

  const items = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of rows) m.set(r.vintage.year, (m.get(r.vintage.year) ?? 0) + 1);
    return [...m.entries()]
      .sort((a, b) => b[0] - a[0])
      .slice(0, 20)
      .map(([year, value]) => ({ label: String(year), value }));
  }, [rows]);

  if (!items.length) return <p className="text-xs text-text-muted">No data yet</p>;
  return <BarChart items={items} color="bg-amber-700" />;
}

// ---------------------------------------------------------------------------
// F12.10 — Age profile (0-5 / 5-10 / 10-20 / 20+)
// ---------------------------------------------------------------------------

function AgeProfile() {
  const { rows } = useInventory(ALL_FILTERS);
  const now = new Date().getFullYear();

  const buckets = useMemo(() => {
    const b = { '0-5 yrs': 0, '5-10 yrs': 0, '10-20 yrs': 0, '20+ yrs': 0 };
    for (const r of rows) {
      const age = now - r.vintage.year;
      if (age < 5) b['0-5 yrs']++;
      else if (age < 10) b['5-10 yrs']++;
      else if (age < 20) b['10-20 yrs']++;
      else b['20+ yrs']++;
    }
    return Object.entries(b).map(([label, value]) => ({ label, value }));
  }, [rows, now]);

  return <BarChart items={buckets} color="bg-sky-700" />;
}

// ---------------------------------------------------------------------------
// F12.11 — Top producers by count
// ---------------------------------------------------------------------------

function TopProducers() {
  const { rows } = useInventory(ALL_FILTERS);

  const items = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      m.set(r.producer.name, (m.get(r.producer.name) ?? 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([label, value]) => ({ label, value }));
  }, [rows]);

  if (!items.length) return <p className="text-xs text-text-muted">No data yet</p>;
  return <BarChart items={items} color="bg-emerald-800" />;
}

// ---------------------------------------------------------------------------
// F12.12 — Drinking window heatmap (next 15 years)
// ---------------------------------------------------------------------------

function DrinkingWindowHeatmap() {
  const { rows } = useInventory(ALL_FILTERS);
  const now = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => now + i);

  const yearData = useMemo(() => {
    return years.map(year => {
      let peak = 0, drinking = 0, entering = 0;
      for (const r of rows) {
        const w = r.vintage.drinking_window_current;
        if (year >= w.from_year && year <= w.to_year) {
          if (year >= w.peak_from && year <= w.peak_to) peak++;
          else drinking++;
        } else if (year === w.from_year - 1) {
          entering++;
        }
      }
      return { year, peak, drinking, entering, total: peak + drinking };
    });
  }, [rows, years]);

  const maxTotal = Math.max(...yearData.map(d => d.total), 1);

  return (
    <div className="space-y-1.5">
      {yearData.map(({ year, peak, drinking, entering, total }) => (
        <div key={year} className="flex items-center gap-2">
          <p className={`text-xs w-10 flex-shrink-0 tabular-nums text-right ${
            year === now ? 'text-gold font-semibold' : 'text-text-muted'
          }`}>{year}</p>
          <div className="flex-1 h-5 bg-surface-3 rounded overflow-hidden flex">
            {peak > 0 && (
              <div
                className="h-full bg-emerald-700"
                style={{ width: `${(peak / maxTotal) * 100}%` }}
                title={`${peak} at peak`}
              />
            )}
            {drinking > 0 && (
              <div
                className="h-full bg-amber-800"
                style={{ width: `${(drinking / maxTotal) * 100}%` }}
                title={`${drinking} in window`}
              />
            )}
          </div>
          <p className="text-xs text-text-muted w-8 tabular-nums text-right">{total}</p>
          {entering > 0 && (
            <span className="text-[11px] text-sky-400 w-16 flex-shrink-0">
              +{entering} entering
            </span>
          )}
        </div>
      ))}
      <div className="flex items-center gap-4 pt-2">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-700 flex-shrink-0" />
          <span className="text-[11px] text-text-muted">Peak</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-800 flex-shrink-0" />
          <span className="text-[11px] text-text-muted">In window</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// F12.13 — Consumption by year
// ---------------------------------------------------------------------------

function ConsumptionByYear() {
  const { rows } = useConsumedInventory();

  function getYear(ts: unknown): number | null {
    if (!ts) return null;
    try {
      const d = typeof (ts as { toDate?: () => Date }).toDate === 'function'
        ? (ts as { toDate: () => Date }).toDate()
        : new Date((ts as { seconds: number }).seconds * 1000);
      return d.getFullYear();
    } catch { return null; }
  }

  const items = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of rows) {
      const y = getYear(r.bottle.status_changed_date);
      if (y) m.set(y, (m.get(y) ?? 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[0] - a[0])
      .slice(0, 8)
      .map(([year, value]) => ({ label: String(year), value }));
  }, [rows]);

  if (!items.length) return <p className="text-xs text-text-muted">No consumed bottles yet</p>;
  return <BarChart items={items} color="bg-claret" />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AnalyticsPage() {
  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3">
        <h1 className="text-base font-semibold text-text-primary">Analytics</h1>
        <p className="text-xs text-text-muted">Full cellar breakdown</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <AnalyticsSection title="Geographic Distribution (F12.7)">
          <GeoDistribution />
        </AnalyticsSection>

        <AnalyticsSection title="Drinking Window Heatmap — Next 15 Years (F12.12)">
          <DrinkingWindowHeatmap />
        </AnalyticsSection>

        <div className="grid sm:grid-cols-2 gap-4">
          <AnalyticsSection title="Top Producers by Count (F12.11)">
            <TopProducers />
          </AnalyticsSection>
          <AnalyticsSection title="Vintage Histogram (F12.9)">
            <VintageHistogram />
          </AnalyticsSection>
        </div>

        <AnalyticsSection title="Age Profile (F12.10)">
          <AgeProfile />
        </AnalyticsSection>

        <AnalyticsSection title="Consumption by Year (F12.13)">
          <ConsumptionByYear />
        </AnalyticsSection>
      </div>
    </div>
  );
}
